// Cart feature: keep a browser-persisted list of configurations, edit their
// quantities, reload one into the editor, share one as a string, and download
// everything as one 3MF (quantities placed as instances) or a zip of every
// item's STLs plus a bom.txt. Storage is IndexedDB (cart-storage.ts); the mesh
// and package builds run at download time, in the worker via the shared
// WorkerClient.

import type { WorkerClient } from "./worker-client.js";
import {
  addItem,
  clearAll,
  CartItem,
  deleteItem,
  loadAll,
  updateQuantity,
} from "./cart-storage.js";
import { encodeShare } from "./share.js";

// The active editor mode supplies what an add stores: the scene JSON and a
// short label for the list and the BOM.
export type CartSource = {
  sceneJson: string;
  label: string;
};

// How one scene is turned into STLs: the export mode and the glue clearance in
// mm (see `generate_scene_stls`).
export type ExportOptions = {
  mode: string;
  clearance: number;
};

type CartCallbacks = {
  // Surface an error via the shared overlay (e.g. an STL build crashed).
  onError: (message: string) => void;
  // Load a scene into the editor and show it (a row click); returns a message
  // when the scene can't be loaded.
  onLoadScene: (sceneJson: string) => string | null;
  // A thumbnail of the view an item is being added from, for its row. Null when
  // the viewer can't produce one.
  captureThumbnail: () => Promise<Blob | null>;
  // How a scene exports, given the cart-wide print settings. Only some kinds of
  // scene take them, which is the editor's business to know, not the cart's.
  exportOptions: (sceneJson: string, settings: ExportOptions) => ExportOptions;
};

export type CartElements = {
  quantityInput: HTMLInputElement;
  addButton: HTMLButtonElement;
  badgeEl: HTMLElement;
  listEl: HTMLElement;
  summaryEl: HTMLElement;
  downloadButton: HTMLButtonElement;
  clearButton: HTMLButtonElement;
  exportFormatSelect: HTMLSelectElement;
  exportModeSelect: HTMLSelectElement;
  glueClearanceInput: HTMLInputElement;
};

// Print settings belong to the cart rather than to any one item, so they
// persist alongside its contents.
const EXPORT_FORMAT_KEY = "synthbash.exportFormat";
const EXPORT_MODE_KEY = "synthbash.exportMode";
const GLUE_CLEARANCE_KEY = "synthbash.glueClearance";

const COPY_LABEL = "Copy";
// Shown when the clipboard can't be written to; the string is selected, so
// copying it is a keystroke away.
const MANUAL_COPY_LABEL = "Press Ctrl/⌘+C";

export class Cart {
  private worker: WorkerClient;
  private source: () => CartSource;
  private cb: CartCallbacks;

  private items: CartItem[] = [];

  // An add is in flight; a second one must wait for the store to settle.
  private adding = false;

  // A download is building from a snapshot of the cart; nothing may change
  // until it lands.
  private busy = false;

  // Object URLs backing the row thumbnails, keyed by item id so rebuilding the
  // rows reuses them; each is revoked when its item leaves the cart.
  private thumbnailUrls = new Map<number, string>();

  // The stored contents are in hand. Until then an add would dedupe against a
  // list that isn't the cart's, and the load would then overwrite it.
  private loaded = false;

  // Set when the store can't be read: what is in the cart is then unknown for
  // the rest of the page's life, so it says so rather than claim to be empty.
  private unavailable: string | null = null;

  private els: CartElements;

  constructor(
    worker: WorkerClient,
    source: () => CartSource,
    els: CartElements,
    cb: CartCallbacks,
  ) {
    this.worker = worker;
    this.source = source;
    this.els = els;
    this.cb = cb;

    this.els.addButton.addEventListener("click", () => this.onAdd());
    this.els.clearButton.addEventListener("click", () => this.onClear());
    this.els.downloadButton.addEventListener("click", () => this.onDownload());
    this.wirePrintSettings();
    this.applyControlState();
  }

  // Load persisted items and render on startup. Storage the browser refuses —
  // or another tab blocking a version upgrade — costs the stored items, not the
  // rest of the app, so it is reported in the list rather than thrown.
  async init() {
    try {
      this.items = await loadAll();
    } catch (err) {
      this.unavailable = (err as Error).message;
    }
    this.loaded = true;
    this.renderList();
  }

  // --- Print settings ------------------------------------------------------

  // The controls hold the settings; localStorage carries them across reloads.
  private wirePrintSettings() {
    wireStoredSelect(this.els.exportFormatSelect, EXPORT_FORMAT_KEY);
    wireStoredSelect(this.els.exportModeSelect, EXPORT_MODE_KEY);
    // The download button names the chosen format.
    this.els.exportFormatSelect.addEventListener("change", () =>
      this.applyControlState(),
    );

    const clearance = readStored(GLUE_CLEARANCE_KEY);
    if (clearance !== null) {
      this.els.glueClearanceInput.value = clearance;
      this.storeClearance();
    }
    this.els.glueClearanceInput.addEventListener("change", () =>
      this.storeClearance(),
    );
  }

  // The clearance the export will use, corrected in the box itself: an emptied,
  // negative or unreadable entry falls back to the markup's default rather than
  // silently printing at something the box doesn't show.
  private glueClearance(): number {
    const input = this.els.glueClearanceInput;
    const entered = Number(input.value);
    const clearance =
      input.value !== "" && Number.isFinite(entered) && entered >= 0
        ? entered
        : Number(input.defaultValue);
    input.value = String(clearance);
    return clearance;
  }

  private storeClearance() {
    writeStored(GLUE_CLEARANCE_KEY, String(this.glueClearance()));
  }

  // The settings every item in a download is printed with.
  private printSettings(): ExportOptions {
    return {
      mode: this.els.exportModeSelect.value,
      clearance: this.glueClearance(),
    };
  }

  // --- Contents ------------------------------------------------------------

  // Adding is a store round-trip, so a second click before it lands would miss
  // the item it is about to dedupe against and split the config in two.
  private async onAdd() {
    if (this.adding) return;
    this.adding = true;
    this.applyControlState();
    try {
      const source = this.source();
      const qty = Math.max(
        1,
        Math.floor(Number(this.els.quantityInput.value) || 1),
      );

      // Dedupe on the scene alone: the same configuration is the same item.
      const existing = this.items.find((it) => it.sceneJson === source.sceneJson);
      if (existing && existing.id != null) {
        // Only count what the store took, so a failed write leaves the list
        // showing what is actually in it.
        const quantity = existing.quantity + qty;
        await updateQuantity(existing.id, quantity);
        existing.quantity = quantity;
        return;
      }

      // Only a new item is worth a capture: a bumped quantity keeps the
      // thumbnail the config was first added with.
      const thumbnail = await this.cb.captureThumbnail();
      const item: CartItem = {
        sceneJson: source.sceneJson,
        label: source.label,
        quantity: qty,
      };
      if (thumbnail) item.thumbnail = thumbnail;
      item.id = await addItem(item);
      this.items.push(item);
    } catch (err) {
      this.cb.onError(`Adding to the cart failed: ${(err as Error).message}`);
    } finally {
      this.adding = false;
      this.renderList();
    }
  }

  // Only the totals need redrawing: the row is already showing the new count,
  // and rebuilding the list would replace the box being typed in.
  private async setQuantity(item: CartItem, quantity: number) {
    if (item.id == null) return;
    try {
      await updateQuantity(item.id, quantity);
      // Only count what the store took, so a failed write leaves the cart
      // showing what is actually in it.
      item.quantity = quantity;
    } catch (err) {
      this.cb.onError(`Updating the cart failed: ${(err as Error).message}`);
    }
    this.renderTotals();
  }

  private async removeItem(item: CartItem, entry: HTMLElement) {
    if (item.id == null) return;
    try {
      await deleteItem(item.id);
    } catch (err) {
      this.cb.onError(`Removing from the cart failed: ${(err as Error).message}`);
      return;
    }
    this.items = this.items.filter((it) => it !== item);
    this.releaseThumbnail(item.id);
    entry.remove();
    this.renderTotals();
  }

  private async onClear() {
    try {
      await clearAll();
    } catch (err) {
      this.cb.onError(`Clearing the cart failed: ${(err as Error).message}`);
      return;
    }
    this.items = [];
    this.releaseThumbnails();
    this.renderList();
  }

  // --- Download ------------------------------------------------------------

  private async onDownload() {
    if (this.items.length === 0) return;
    this.setBusy(true);
    try {
      // One reading of the contents and the print settings covers the whole
      // download, so nothing landing while it builds ends up in half of it.
      const items = [...this.items];
      const settings = this.printSettings();
      if (this.els.exportFormatSelect.value === "zip") {
        await this.downloadZip(items, settings);
      } else {
        await this.download3mf(items, settings);
      }
    } catch (err) {
      this.cb.onError(`Building the download failed: ${(err as Error).message}`);
    } finally {
      this.setBusy(false);
    }
  }

  // One 3MF package for the whole cart: the worker builds every item's solids
  // there and then, placing quantity-many copies of each.
  private async download3mf(items: CartItem[], settings: ExportOptions) {
    const bytes = await this.worker.request3mf({
      items: items.map((item) => {
        const { mode, clearance } = this.cb.exportOptions(
          item.sceneJson,
          settings,
        );
        return {
          scene: item.sceneJson,
          mode,
          clearance,
          quantity: item.quantity,
          label: item.label,
        };
      }),
    });
    triggerDownload(bytes, "synthbash.3mf", "model/3mf");
  }

  private async downloadZip(items: CartItem[], settings: ExportOptions) {
    // Every item's files go into one zip, each prefixed by its item index so
    // names stay unique across items that share an inner filename.
    const filenames: string[] = [];
    const stls: Uint8Array[] = [];
    const bomLines: string[] = [];
    for (const [index, item] of items.entries()) {
      const { mode, clearance } = this.cb.exportOptions(
        item.sceneJson,
        settings,
      );
      const files = await this.worker.requestStls({
        scene: item.sceneJson,
        mode,
        clearance,
      });
      if (files.length === 0) {
        throw new Error(`"${item.label}" produced no STL`);
      }
      const names = files.map((file) => `${index + 1}-${file.filename}`);
      filenames.push(...names);
      stls.push(...files.map((file) => file.bytes));
      // One BOM line per item: "3 x 32mm base + dirt: 1-base.stl, 1-topper.stl".
      bomLines.push(`${item.quantity} x ${item.label}: ${names.join(", ")}`);
    }
    const zip = await this.worker.requestZip({
      filenames,
      stls,
      bomText: bomLines.join("\n") + "\n",
    });
    triggerDownload(zip, "synthbash.zip", "application/zip");
  }

  private setBusy(busy: boolean) {
    this.busy = busy;
    this.applyControlState();
  }

  // The enabled state of every control, in one place so a render (which builds
  // the row controls afresh) and a running download can't disagree: while a
  // download builds nothing may change the contents or the settings out from
  // under the captured job, and the whole-cart buttons need something to act on.
  private applyControlState() {
    const empty = this.items.length === 0;
    // Adding needs the cart's real contents: without them a new item would
    // dedupe against the wrong list.
    const usable = this.loaded && this.unavailable === null;
    this.els.addButton.disabled = !usable || this.busy || this.adding;
    this.els.clearButton.disabled = this.busy || empty;
    this.els.downloadButton.disabled = this.busy || empty;
    this.els.downloadButton.textContent = this.busy
      ? "Building…"
      : this.els.exportFormatSelect.value === "zip"
        ? "Download zip"
        : "Download 3MF";
    this.els.exportFormatSelect.disabled = this.busy;
    this.els.exportModeSelect.disabled = this.busy;
    this.els.glueClearanceInput.disabled = this.busy;
    const rowControls = this.els.listEl.querySelectorAll<
      HTMLInputElement | HTMLButtonElement
    >(".cart-qty, .cart-remove");
    for (const control of rowControls) {
      control.disabled = this.busy;
    }
  }

  // --- Rendering -----------------------------------------------------------

  // Rebuild every row. Only for changes to what the cart holds: a quantity edit
  // updates its own row, so the box being typed in survives.
  private renderList() {
    this.els.listEl.textContent = "";
    for (const item of this.items) {
      this.els.listEl.appendChild(this.rowFor(item));
    }
    this.renderTotals();
  }

  // The counts and every control's enabled state — all a quantity edit or a
  // finished download moves.
  private renderTotals() {
    const pieceCount = this.items.reduce((sum, it) => sum + it.quantity, 0);
    this.els.summaryEl.textContent = this.unavailable
      ? `Cart unavailable (${this.unavailable}); reload to try again.`
      : `${plural(this.items.length, "item")}, ${plural(pieceCount, "piece")}`;

    this.els.badgeEl.textContent = String(this.items.length);
    this.els.badgeEl.hidden = this.items.length === 0;

    this.applyControlState();
  }

  private rowFor(item: CartItem): HTMLElement {
    const entry = document.createElement("div");
    entry.className = "cart-item";

    const row = document.createElement("div");
    row.className = "cart-row";
    row.appendChild(this.thumbnailFor(item));

    const label = document.createElement("button");
    label.type = "button";
    label.className = "cart-label";
    label.title = "Edit this item";
    // The quantity box next to it already says how many.
    label.textContent = item.label;
    label.addEventListener("click", () => {
      const error = this.cb.onLoadScene(item.sceneJson);
      if (error) this.cb.onError(`Could not load "${item.label}": ${error}`);
    });
    row.appendChild(label);

    const qty = document.createElement("input");
    qty.type = "number";
    qty.min = "0";
    qty.step = "1";
    qty.value = String(item.quantity);
    qty.className = "cart-qty";
    qty.addEventListener("change", async () => {
      // An emptied or unreadable box is not a quantity; zero is a deliberate
      // "take it out", and goes through.
      const entered = Number(qty.value);
      if (qty.value !== "" && Number.isFinite(entered)) {
        const wanted = Math.floor(entered);
        if (wanted <= 0) await this.removeItem(item, entry);
        else await this.setQuantity(item, wanted);
      }
      // The box ends up showing what the item actually holds: a fractional
      // entry rounded, or the old count where the store refused the change.
      qty.value = String(item.quantity);
    });
    row.appendChild(qty);

    const share = this.shareBoxFor(item);
    const shareButton = document.createElement("button");
    shareButton.type = "button";
    shareButton.textContent = "Share";
    shareButton.addEventListener("click", share.toggle);
    row.appendChild(shareButton);

    const remove = document.createElement("button");
    remove.type = "button";
    remove.className = "cart-remove";
    remove.textContent = "×";
    remove.title = "Remove";
    remove.addEventListener("click", () => this.removeItem(item, entry));
    row.appendChild(remove);

    entry.append(row, share.box);
    return entry;
  }

  // A row's picture: the stored snapshot, or the empty box the styling gives an
  // item that has none. Decorative, so it stays out of the accessible name the
  // label already supplies.
  private thumbnailFor(item: CartItem): HTMLElement {
    if (!item.thumbnail || item.id == null) {
      const placeholder = document.createElement("div");
      placeholder.className = "cart-thumb";
      return placeholder;
    }
    const img = document.createElement("img");
    img.className = "cart-thumb";
    img.alt = "";
    img.src = this.thumbnailUrl(item.id, item.thumbnail);
    return img;
  }

  // One URL per item, so a rebuilt row shows the picture it already had rather
  // than loading a fresh one.
  private thumbnailUrl(id: number, thumbnail: Blob): string {
    let url = this.thumbnailUrls.get(id);
    if (url === undefined) {
      url = URL.createObjectURL(thumbnail);
      this.thumbnailUrls.set(id, url);
    }
    return url;
  }

  private releaseThumbnail(id: number) {
    const url = this.thumbnailUrls.get(id);
    if (url === undefined) return;
    URL.revokeObjectURL(url);
    this.thumbnailUrls.delete(id);
  }

  private releaseThumbnails() {
    for (const url of this.thumbnailUrls.values()) URL.revokeObjectURL(url);
    this.thumbnailUrls.clear();
  }

  // A row's share reveal: the encoded scene plus a copy button. Encoding is
  // async, so the string is filled in the first time the row is revealed.
  private shareBoxFor(item: CartItem): { box: HTMLElement; toggle: () => void } {
    const box = document.createElement("div");
    box.className = "cart-share";
    box.hidden = true;

    const text = document.createElement("input");
    text.type = "text";
    text.readOnly = true;
    text.className = "cart-share-text";
    box.appendChild(text);

    const copy = document.createElement("button");
    copy.type = "button";
    copy.textContent = COPY_LABEL;
    copy.addEventListener("click", () => {
      // Selecting doubles as feedback and as the manual fallback: the clipboard
      // API needs a secure origin, which the plain-http LAN address a phone
      // reaches this on is not, so there it isn't defined at all.
      text.select();
      const clipboard: Clipboard | undefined = navigator.clipboard;
      if (!clipboard) {
        flashCopy(copy, MANUAL_COPY_LABEL);
        return;
      }
      clipboard.writeText(text.value).then(
        () => flashCopy(copy, "Copied"),
        () => flashCopy(copy, MANUAL_COPY_LABEL),
      );
    });
    box.appendChild(copy);

    const toggle = () => {
      box.hidden = !box.hidden;
      if (box.hidden || text.value) return;
      encodeShare(item.sceneJson).then(
        (encoded) => (text.value = encoded),
        (err: Error) => this.cb.onError(`Could not build a share string: ${err.message}`),
      );
    };
    return { box, toggle };
  }
}

// --- helpers --------------------------------------------------------------

// localStorage throws outright where the browser blocks site storage, and the
// print settings are only a convenience: fall back to the markup's defaults for
// this session rather than take the whole cart down with it.
function readStored(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStored(key: string, value: string) {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Unreachable storage: the setting still applies until the page reloads.
  }
}

// Restore a select from localStorage and persist its changes. A stored value
// that is no longer an option blanks the select; fall back to the first one and
// rewrite it, so the stale value doesn't outlive the reload.
function wireStoredSelect(select: HTMLSelectElement, key: string) {
  const stored = readStored(key);
  if (stored) {
    select.value = stored;
    if (!select.value) {
      select.selectedIndex = 0;
      writeStored(key, select.value);
    }
  }
  select.addEventListener("change", () => writeStored(key, select.value));
}

// "1 piece" / "3 pieces".
function plural(count: number, word: string): string {
  return `${count} ${word}${count === 1 ? "" : "s"}`;
}

// Briefly show feedback in place of the copy button's label.
function flashCopy(button: HTMLButtonElement, text: string) {
  button.textContent = text;
  setTimeout(() => (button.textContent = COPY_LABEL), 1200);
}

// Copy into a fresh ArrayBuffer (wasm-bindgen returns Uint8Array views over
// possibly-SharedArrayBuffer-typed memory, which the Blob/BlobPart types
// reject; a copy is also what we want so we don't retain wasm memory).
function toArrayBuffer(bytes: Uint8Array): ArrayBuffer {
  const copy = new Uint8Array(bytes.length);
  copy.set(bytes);
  return copy.buffer;
}

function triggerDownload(bytes: Uint8Array, filename: string, mimeType: string) {
  const blob = new Blob([toArrayBuffer(bytes)], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoke after the click has had a chance to start the download.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
